/**
 * The currency picker's options.
 *
 * Built from `Intl`, not from a table in this repo. A hand-kept list of three
 * hundred ISO codes is a list that goes stale and, worse, a list that is
 * missing exactly the currency of whoever it fails for. The browser already
 * ships the codes and their names in the reader's own language.
 *
 * The DECIMALS still come from `@et/core`. `decimalsFor` is what turns 4732
 * into "47.32" everywhere in this app, so it — not Intl's separate opinion —
 * is what decides which currencies are interchangeable here. Two sources of
 * truth for the decimal point is how a ledger starts printing lies.
 */
import { decimalsFor } from '@et/core';

export interface CurrencyOption {
  code: string;
  /** "EUR — Euro", ready to print. */
  label: string;
  decimals: number;
}

/**
 * Codes `Intl` lists that are not money you spend: precious metals, IMF
 * drawing rights, the testing and "no currency" placeholders, and the old
 * European units of account. Offering XAU next to USD in a household's
 * settings is noise at best.
 */
const NOT_SPENDABLE = new Set([
  'XAU', 'XAG', 'XPD', 'XPT',
  'XDR', 'XUA', 'XSU',
  'XBA', 'XBB', 'XBC', 'XBD',
  'XTS', 'XXX', 'XFU', 'XFO',
]);

/**
 * Enough to keep the picker useful if `Intl.supportedValuesOf` is missing —
 * it is a fairly recent addition, and a settings screen that offers nothing is
 * worse than one that offers the obvious dozen.
 */
const FALLBACK = [
  'AED', 'AFN', 'AUD', 'BRL', 'CAD', 'CHF', 'CNY', 'CZK', 'DKK', 'EGP', 'EUR',
  'GBP', 'HUF', 'IDR', 'ILS', 'INR', 'IRR', 'JPY', 'KRW', 'MAD', 'MXN', 'MYR',
  'NGN', 'NOK', 'NZD', 'PHP', 'PKR', 'PLN', 'QAR', 'RON', 'RUB', 'SAR', 'SEK',
  'SGD', 'THB', 'TRY', 'UAH', 'USD', 'VND', 'ZAR',
];

function codes(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  try {
    const listed = intl.supportedValuesOf?.('currency');
    if (listed && listed.length > 0) return listed;
  } catch {
    /* an engine that lists nothing is one we fall back for */
  }
  return FALLBACK;
}

/** `Intl.DisplayNames` in the reader's locale, or the bare code if it cannot. */
function namer(): (code: string) => string {
  try {
    const names = new Intl.DisplayNames(undefined, { type: 'currency' });
    return (code) => names.of(code) ?? code;
  } catch {
    return (code) => code;
  }
}

let cache: CurrencyOption[] | null = null;

/**
 * Every spendable currency, sorted by code — people know their own code and
 * scan for it, while the name is what confirms they found the right one.
 */
export function currencyOptions(): CurrencyOption[] {
  if (cache) return cache;

  const name = namer();
  const out: CurrencyOption[] = [];

  for (const code of codes()) {
    if (!/^[A-Z]{3}$/.test(code) || NOT_SPENDABLE.has(code)) continue;
    let decimals: number;
    try {
      decimals = decimalsFor(code);
    } catch {
      continue;
    }
    const shown = name(code);
    out.push({ code, label: shown === code ? code : `${code} — ${shown}`, decimals });
  }

  out.sort((a, b) => a.code.localeCompare(b.code));
  cache = out;
  return out;
}

/**
 * What the household may switch to.
 *
 * With an empty ledger, anything. With entries on file, only currencies
 * written with the same number of decimals — an amount is stored as minor
 * units with no record of its currency, so crossing decimal groups would
 * republish the whole ledger a hundred times off (see `members.service.ts`).
 * The current currency is always present, whatever the filter says, because a
 * picker that does not show what is selected is broken.
 */
export function currencyChoices(current: string, recordedEntries: number): CurrencyOption[] {
  const all = currencyOptions();
  if (recordedEntries === 0) return withCurrent(all, current);

  let decimals: number;
  try {
    decimals = decimalsFor(current);
  } catch {
    return withCurrent(all, current);
  }
  return withCurrent(
    all.filter((option) => option.decimals === decimals),
    current,
  );
}

function withCurrent(options: CurrencyOption[], current: string): CurrencyOption[] {
  if (options.some((option) => option.code === current)) return options;
  return [{ code: current, label: current, decimals: 2 }, ...options];
}
